import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { X, Save, Link2, FileText, Video, File, Music, Globe } from "lucide-react";
import { useDesignSystem, cn } from './designSystem';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

const ResourceDialogForm = ({ 
  isOpen, 
  onClose, 
  initialResource, 
  onSubmit, 
  isEditing = false, 
  isSaving = false 
}) => {
  const { colors, typography, effects, componentStyles } = useDesignSystem();
  
  const defaultFormData = {
    title: '',
    url: '',
    description: '',
    type: 'video',
    language: 'no',
    tags: []
  };
  
  const [formData, setFormData] = useState(defaultFormData);
  const [tagInput, setTagInput] = useState('');
  
  // Når dialogen åpnes settes formData én gang
  useEffect(() => {
    if (isOpen) {
      if (initialResource) {
        setFormData({
          title: initialResource.title || '',
          url: initialResource.url || '',
          description: initialResource.description || '',
          type: initialResource.type || 'video',
          language: initialResource.language || 'no',
          tags: Array.isArray(initialResource.tags) ? [...initialResource.tags] : []
        });
      } else {
        setFormData(defaultFormData);
      }
    }
  }, [isOpen]);
  
  // Reset tag-input når dialogen lukkes
  useEffect(() => {
    if (!isOpen) {
      setTagInput('');
    }
  }, [isOpen]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    const finalFormData = { ...formData };
    if (tagInput.trim()) {
      addTag(tagInput.trim());
      finalFormData.tags = [...formData.tags, tagInput.trim()];
      setTagInput('');
    }
    console.log('Submitting form data:', finalFormData);
    onSubmit(finalFormData);
  };

  const addTag = (tag) => {
    if (!tag) return;
    if (!formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
    setTagInput('');
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      addTag(tagInput.trim());
    } else if (e.key === 'Backspace' && !tagInput && formData.tags.length > 0) {
      removeTag(formData.tags[formData.tags.length - 1]);
    }
  };

  const getTagColor = (tag) => {
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      hash = tag.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorOptions = [
      cn(colors.primary.light, colors.primary.text),
      cn(colors.success.light, colors.success.text),
      cn(colors.warning.light, colors.warning.text),
      "bg-purple-50 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300",
      "bg-pink-50 text-pink-700 dark:bg-pink-900/50 dark:text-pink-300",
      "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300",
      "bg-teal-50 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300",
      "bg-lime-50 text-lime-700 dark:bg-lime-900/50 dark:text-lime-300",
      "bg-sky-50 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300",
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
      "bg-amber-50 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
      "bg-slate-50 text-slate-700 dark:bg-slate-900/50 dark:text-slate-300",
      "bg-rose-50 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300",
      "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300",
      "bg-fuchsia-50 text-fuchsia-700 dark:bg-fuchsia-900/50 dark:text-fuchsia-300",
      "bg-orange-50 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300",
      "bg-blue-400/20 text-blue-800 dark:bg-blue-800/40 dark:text-blue-200",
    ];
    
    return colorOptions[Math.abs(hash) % colorOptions.length];
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className={cn(typography.h3)}>
            {isEditing ? 'Rediger ressurs' : 'Legg til ressurs'}
          </DialogTitle>
          <DialogDescription className={cn(typography.small, colors.neutral.textSecondary)}>
            {isEditing ? 'Oppdater informasjonen for ressursen.' : 'Fyll ut informasjonen under for å legge til en ny ressurs.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-grow overflow-y-auto py-2">
          <ScrollArea className="h-full pr-4">
            <form id="resource-form" onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Tittel */}
                <div className="space-y-2">
                  <label htmlFor="title" className={cn(typography.label)}>Tittel</label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Ressursens tittel"
                    required
                    className={cn(typography.inputText, "w-full")}
                  />
                </div>
                
                {/* URL */}
                <div className="space-y-2">
                  <label htmlFor="url" className={cn(typography.label)}>URL</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Link2 className="h-4 w-4 text-gray-500 dark:text-neutral-400" />
                    </div>
                    <Input
                      id="url"
                      value={formData.url}
                      onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="https://eksempel.no/ressurs"
                      required
                      className={cn(typography.inputText, "pl-10 w-full")}
                      type="url"
                    />
                  </div>
                </div>
                
                {/* Type og Språk */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label htmlFor="type" className={cn(typography.label)}>Type</label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                    >
                      <SelectTrigger id="type" className="w-full">
                        <SelectValue placeholder="Velg type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="article">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            <span>Artikkel</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="video">
                          <div className="flex items-center gap-2">
                            <Video className="h-4 w-4" />
                            <span>Video</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="pdf">
                          <div className="flex items-center gap-2">
                            <File className="h-4 w-4" />
                            <span>PDF</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="lydfil">
                          <div className="flex items-center gap-2">
                            <Music className="h-4 w-4" />
                            <span>Lydfil</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="other">
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4" />
                            <span>Annet</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="language" className={cn(typography.label)}>Språk</label>
                    <Select
                      value={formData.language}
                      onValueChange={(value) => {
                        console.log('Language changed to:', value);
                        setFormData(prev => ({ ...prev, language: value }));
                      }}
                    >
                      <SelectTrigger id="language" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">
                          <div className="flex items-center gap-2">
                            <span>🇳🇴</span>
                            <span>Norsk</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="en">
                          <div className="flex items-center gap-2">
                            <span>🇬🇧</span>
                            <span>Engelsk</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="other">
                          <div className="flex items-center gap-2">
                            <span>🌐</span>
                            <span>Annet</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Beskrivelse */}
                <div className="space-y-2">
                  <label htmlFor="description" className={cn(typography.label)}>Beskrivelse</label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Skriv en kort beskrivelse av ressursen"
                    required
                    className={cn(typography.inputText, "min-h-[80px] resize-none w-full")}
                  />
                </div>
                
                {/* Tags */}
                <div className="space-y-2">
                  <label htmlFor="tags" className={cn(typography.label)}>Tags</label>
                  <div className={cn(
                    "flex flex-wrap gap-1.5 p-2 border rounded-md min-h-[42px] w-full",
                    colors.neutral.border,
                    colors.neutral.bg,
                    "focus-within:ring-2 focus-within:ring-blue-500 dark:focus-within:ring-blue-400"
                  )}>
                    {formData.tags.map(tag => (
                      <span 
                        key={tag} 
                        className={cn(
                          getTagColor(tag),
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                        )}
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-1 h-3.5 w-3.5 rounded-full inline-flex items-center justify-center text-current opacity-60 hover:opacity-100"
                        >
                          <X className="h-2 w-2" />
                        </button>
                      </span>
                    ))}
                    <input
                      id="tags"
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      className={cn(
                        "flex-grow outline-none min-w-[80px] text-sm bg-transparent",
                        colors.neutral.textPrimary
                      )}
                      placeholder={formData.tags.length ? "" : "Skriv tags og trykk Enter..."}
                    />
                  </div>
                  <p className={cn(typography.small, colors.neutral.textTertiary)}>
                    Skriv tag og trykk komma eller enter for å legge til
                  </p>
                </div>
              </div>
            </form>
          </ScrollArea>
        </div>
        
        <DialogFooter className="pt-4 border-t dark:border-neutral-800 sm:flex-row flex-col gap-2 sm:gap-0">
          <div className="w-full sm:w-auto order-2 sm:order-1">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onClose(false)}
              className={cn(componentStyles.button.secondary, "w-full sm:w-auto")}
            >
              Avbryt
            </Button>
          </div>
          <div className="w-full sm:w-auto order-1 sm:order-2">
            <Button 
              type="submit"
              form="resource-form"
              disabled={isSaving}
              className={cn(
                componentStyles.button.primary,
                isSaving && componentStyles.button.states.loading,
                "w-full sm:w-auto"
              )}
            >
              {isSaving ? (
                <>
                  <div className={cn(
                    "mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full",
                    effects.spin
                  )}></div>
                  Lagrer...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {isEditing ? 'Oppdater' : 'Lagre'}
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ResourceDialogForm;